// Règles métier du recrutement des volontaires (sans dépendance Nuxt : testé via node:test).
import { isValidNationalRegister, normalizeNationalRegister } from './national-register.ts'

/** Policy Directus « Recrutement » (créée avec cet identifiant par `provision-recruitment-directus.mjs`). */
export const RECRUITMENT_POLICY_ID = '20cde0a8-c1e4-4a5e-bd02-715f9ea30ca4'

/** Policy « Responsable de service » (responsable de pôle) : requise, avec RH ou Recrutement, pour modifier les VOCAN. */
export const SERVICE_MANAGER_POLICY_ID = 'bcc67305-7e37-454e-a05d-36df5d7b4790'

export const MIN_AGE = 16
export const ADULT_AGE = 18
export const MAX_UPLOAD_BYTES = 10 * 1024 * 1024
export const MAX_FILES_PER_PIECE = 6
export const FILE_TOKEN_TTL_DAYS = 30
export const FILE_MAX_ATTEMPTS = 5
export const RESPONSE_TARGET_DAYS = 7
export const REMINDER_DAYS = [7, 14] as const
export const EXPIRY_DAYS = 30
export const PURGE_DELAY_DAYS = 30
export const ANONYMIZE_AFTER_DAYS = 365

export const APPLICATION_STATUSES = {
  new: 'Nouvelle',
  review: 'En étude',
  refused_motivation: 'Refusée (motivation)',
  refused_qualification: 'Refusée (qualification)',
  to_complete: 'Dossier à compléter',
  to_verify: 'Pièces à vérifier',
  conform: 'Dossier conforme',
  onboarding: 'Intégration en cours',
  integrated: 'Intégré(e)',
  expired: 'Expirée ou retirée',
} as const
export type ApplicationStatus = keyof typeof APPLICATION_STATUSES

export const PIPELINE_STATUSES: ApplicationStatus[] = ['new', 'review', 'to_complete', 'to_verify', 'conform', 'onboarding']
export const CLOSED_STATUSES: ApplicationStatus[] = ['refused_motivation', 'refused_qualification', 'expired', 'integrated']

// ── Fonctions et brevets ─────────────────────────────────────────────────────

export const FUNCTIONS = {
  secours: 'Secours',
  logistique: 'Logisticien',
  administratif: 'Administratif',
  formateur: 'Formateur aux premiers secours',
  communication: 'Communication',
} as const
export type FunctionKey = keyof typeof FUNCTIONS

/** Brevet obligatoire pour ces fonctions, facultatif pour les autres. */
export const BREVET_REQUIRED_FUNCTIONS: FunctionKey[] = ['secours', 'formateur']

export const BREVETS = {
  beps: { label: 'BEPS', numberRequired: false, inami: false, skill: 21 },
  secouriste: { label: 'Secouriste', numberRequired: false, inami: false, skill: 21 },
  atnup: { label: 'ATNUP', numberRequired: true, inami: false, skill: 22, numberLabel: 'N° de visa (SPF Santé publique)' },
  amu: { label: 'AMU', numberRequired: true, inami: false, skill: 24, numberLabel: 'N° de badge' },
  infirmier: { label: 'Infirmier', numberRequired: true, inami: true, skill: 23, numberLabel: 'N° INAMI (11 chiffres)' },
  siamu: { label: 'Infirmier SIAMU', numberRequired: true, inami: true, skill: 25, numberLabel: 'N° INAMI (11 chiffres)' },
  medecin: { label: 'Médecin', numberRequired: true, inami: true, skill: 26, numberLabel: 'N° INAMI (11 chiffres)' },
} as const
export type BrevetKey = keyof typeof BREVETS

/** Compétences eBrigade des fonctions retenues (formateur 15, administratif 44, communication 45, logistique 18, secours 51). */
export const FUNCTION_SKILLS: Record<FunctionKey, number> = { secours: 51, logistique: 18, administratif: 44, formateur: 15, communication: 45 }

export interface DeclaredBrevet { type: BrevetKey, number: string, verified?: boolean, expiresAt?: string | null }

/** Date `YYYY-MM-DD` valide et non passée, ou null. */
export const parseExpiry = (value: unknown, now = new Date()): string | null => {
  const text = String(value ?? '').trim()
  if (!/^\d{4}-\d{2}-\d{2}$/.test(text) || Number.isNaN(Date.parse(text))) return null
  const year = Number(text.slice(0, 4))
  return text >= now.toISOString().slice(0, 10) && year <= now.getUTCFullYear() + 30 ? text : null
}

// ── Centres ──────────────────────────────────────────────────────────────────

/** Section eBrigade → libellé, dossier Nextcloud de destination. */
export const RECRUITMENT_CENTERS = {
  2: { label: 'CS Luxembourg', folder: 'CS Luxembourg' },
  7: { label: 'CS Hainaut - Namur', folder: 'CS Hainaut' },
  9: { label: 'CS Liège', folder: 'CS Luxembourg' },
  10: { label: 'Département Formation', folder: 'Formation' },
  3: { label: 'Équipe Médicale', folder: 'Equipe Médicale' },
} as const
export type CenterId = keyof typeof RECRUITMENT_CENTERS

export const isCenterId = (value: unknown): value is CenterId => Number(value) in RECRUITMENT_CENTERS

/** Recommandation de centre selon le code postal belge (le recruteur choisit explicitement). */
export const suggestCenter = (postalCode: unknown): CenterId | null => {
  const code = Number(String(postalCode ?? '').trim())
  if (!Number.isInteger(code)) return null
  if (code >= 6600 && code <= 6999) return 2
  if (code >= 4000 && code <= 4999) return 9
  if ((code >= 5000 && code <= 5999) || (code >= 6000 && code <= 6599) || (code >= 7000 && code <= 7999)) return 7
  return null
}

/** Explication lisible de la recommandation de centre (affichée au recruteur). */
export const explainCenter = (postalCode: unknown, city = ''): string => {
  const code = Number(String(postalCode ?? '').trim())
  const where = `Code postal ${String(postalCode ?? '').trim() || '?'}${city ? ` (${city})` : ''}`
  if (!Number.isInteger(code)) return 'Aucune recommandation : code postal inconnu.'
  if (code >= 6600 && code <= 6999) return `${where} : province de Luxembourg (6600 à 6999) → CS Luxembourg.`
  if (code >= 4000 && code <= 4999) return `${where} : province de Liège (4000 à 4999) → CS Liège, dont le dossier Nextcloud est « CS Luxembourg ».`
  if (code >= 5000 && code <= 5999) return `${where} : province de Namur (5000 à 5999) → CS Hainaut - Namur.`
  if ((code >= 6000 && code <= 6599) || (code >= 7000 && code <= 7999)) return `${where} : province de Hainaut (6000 à 6599, 7000 à 7999) → CS Hainaut - Namur.`
  return `Aucune recommandation : ${where} est hors des zones d'intervention (Hainaut, Namur, Liège, Luxembourg). Le recruteur choisit.`
}

// ── Pièces du dossier ────────────────────────────────────────────────────────

export const PIECES = {
  id_card: { label: 'Carte d\'identité (recto et verso)', folder: '003 Permis - CI', required: true },
  photo: { label: 'Photo d\'identité', folder: '007 Photo', required: true },
  medical: { label: 'Certificat d\'aptitude et de vaccination', folder: '004 Attestation médicale', required: true, template: 'https://cloud.ambulancepresent.be/s/sb8zK26q9s7zBL7', hint: 'À faire remplir par votre médecin.' },
  criminal_record: { label: 'Extrait de casier judiciaire (modèle 2)', folder: '005 Extrait de casier judiciaire', required: true },
  // Envoyée et signée électroniquement par les RH APRÈS « Dossier conforme » : jamais demandée au candidat, ne bloque pas la conformité.
  convention: { label: 'Convention de volontariat, ROI et droit à l\'image signés', folder: '001 Convention Volontariat-ROI-Droit Image', required: false, candidate: false },
  brevet: { label: 'Copie de chaque brevet déclaré', folder: '002 Brevet', required: true },
  license: { label: 'Permis de conduire (recto et verso, facultatif)', folder: '003 Permis - CI', required: false },
  parental: { label: 'Autorisation parentale (moins de 18 ans)', folder: '008 Autre', required: true, template: 'https://cloud.ambulancepresent.be/s/zTnDCk3nH93NnRG', hint: 'À faire signer par un parent ou tuteur légal.' },
} as const
export type PieceKey = keyof typeof PIECES

export type PieceStatus = 'pending' | 'uploaded' | 'validated' | 'rejected'
export interface PieceState { status: PieceStatus, files: string[], reason?: string }
export type PiecesState = Partial<Record<PieceKey, PieceState>>

export const isPieceKey = (value: unknown): value is PieceKey => typeof value === 'string' && value in PIECES

/** Pièces applicables à un candidat. */
export const applicablePieces = (input: { birthdate: string, brevets: DeclaredBrevet[] }, now = new Date()): PieceKey[] => {
  const keys = (Object.keys(PIECES) as PieceKey[]).filter((key) => {
    if (key === 'parental') return isMinor(input.birthdate, now)
    if (key === 'brevet') return input.brevets.length > 0
    return true
  })
  return keys
}

/** Pièces que le candidat dépose lui-même (la convention est gérée par les RH). */
export const candidatePieces = (input: { birthdate: string, brevets: DeclaredBrevet[] }, now = new Date()): PieceKey[] =>
  applicablePieces(input, now).filter(key => (PIECES[key] as { candidate?: boolean }).candidate !== false)

export const requiredPieces = (input: { birthdate: string, brevets: DeclaredBrevet[] }, now = new Date()): PieceKey[] =>
  applicablePieces(input, now).filter(key => PIECES[key].required)

/** « Dossier conforme » possible : toutes les pièces obligatoires validées. */
export const isDossierConform = (input: { birthdate: string, brevets: DeclaredBrevet[] }, pieces: PiecesState, now = new Date()): boolean =>
  requiredPieces(input, now).every(key => pieces[key]?.status === 'validated')

/** Toutes les pièces obligatoires déposées (ou déjà validées) : le dossier passe en vérification. */
export const isDossierComplete = (input: { birthdate: string, brevets: DeclaredBrevet[] }, pieces: PiecesState, now = new Date()): boolean =>
  candidatePieces(input, now).filter(key => PIECES[key].required).every(key => ['uploaded', 'validated'].includes(pieces[key]?.status ?? 'pending'))

// ── Âge ──────────────────────────────────────────────────────────────────────

export const ageOn = (birthdate: string, now = new Date()): number | null => {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(birthdate || ''))
  if (!match) return null
  const [year, month, day] = [Number(match[1]), Number(match[2]), Number(match[3])]
  let age = now.getUTCFullYear() - year
  if (now.getUTCMonth() + 1 < month || (now.getUTCMonth() + 1 === month && now.getUTCDate() < day)) age -= 1
  return age
}

export const isMinor = (birthdate: string, now = new Date()): boolean => {
  const age = ageOn(birthdate, now)
  return age !== null && age < ADULT_AGE
}

export const isValidNationalRegisterOrEmpty = (value: string): boolean =>
  !value || isValidNationalRegister(value)

// ── Validation de la candidature ─────────────────────────────────────────────

export const NAME_RE = /^[\p{L}][\p{L}\p{M}' .-]{0,79}$/u
export const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/
export const BE_PHONE_RE = /^(?:\+32|0032|0)[1-9]\d{7,8}$/

/** Téléphone au format national `0470123456`. */
export const toNationalPhone = (value: string): string => {
  const digits = String(value || '').replace(/[\s./()-]/g, '')
  if (digits.startsWith('+32')) return `0${digits.slice(3)}`
  if (digits.startsWith('0032')) return `0${digits.slice(4)}`
  return digits
}

export interface ApplicationInput {
  firstName: string
  lastName: string
  email: string
  phone: string
  birthdate: string
  address: string
  postalCode: string
  city: string
  nationalRegister: string
  functions: FunctionKey[]
  brevets: DeclaredBrevet[]
  motivation: string
  source: string
}

const clean = (value: unknown, max: number) =>
  // eslint-disable-next-line no-control-regex -- suppression volontaire des caractères de contrôle
  String(value ?? '').replace(/[\u0000-\u0008\u000B\u000C\u000E-\u001F\u007F]/g, '').trim().slice(0, max)

const titleCase = (value: string) => value.toLowerCase().replace(/(^|[\s\-'])\p{L}/gu, m => m.toUpperCase())

/** Normalise et valide le corps du formulaire public. Renvoie la première erreur par champ. */
export const parseApplication = (raw: Record<string, unknown>, now = new Date()): { value?: ApplicationInput, errors: Record<string, string> } => {
  const errors: Record<string, string> = {}
  const firstName = titleCase(clean(raw.firstName, 80))
  const lastName = titleCase(clean(raw.lastName, 80))
  const email = clean(raw.email, 254).toLowerCase()
  const phone = toNationalPhone(clean(raw.phone, 30))
  const birthdate = clean(raw.birthdate, 10)
  const address = clean(raw.address, 200)
  const postalCode = clean(raw.postalCode, 10)
  const city = clean(raw.city, 100)
  const nationalRegister = normalizeNationalRegister(clean(raw.nationalRegister, 20))
  const motivation = clean(raw.motivation, 3000)
  const source = clean(raw.source, 200)

  if (!NAME_RE.test(firstName)) errors.firstName = 'Prénom invalide.'
  if (!NAME_RE.test(lastName)) errors.lastName = 'Nom invalide.'
  if (!EMAIL_RE.test(email)) errors.email = 'Adresse e-mail invalide.'
  if (!BE_PHONE_RE.test(phone)) errors.phone = 'Numéro de téléphone belge invalide.'
  const age = ageOn(birthdate, now)
  if (age === null || age > 100) errors.birthdate = 'Date de naissance invalide.'
  else if (age < MIN_AGE) errors.birthdate = `Il faut avoir au moins ${MIN_AGE} ans pour postuler.`
  if (address.length < 3 || !/^\d{4}$/.test(postalCode) || city.length < 2) errors.address = 'Adresse complète requise (rue, numéro, code postal, commune).'
  if (!nationalRegister) errors.nationalRegister = 'Le numéro de registre national est requis.'
  else if (!isValidNationalRegister(nationalRegister)) errors.nationalRegister = 'Numéro de registre national invalide (11 chiffres, clé de contrôle incorrecte).'

  const functions = [...new Set((Array.isArray(raw.functions) ? raw.functions : []).map(String))].filter((f): f is FunctionKey => f in FUNCTIONS)
  if (!functions.length) errors.functions = 'Choisissez au moins une fonction.'

  const brevets: DeclaredBrevet[] = []
  for (const item of Array.isArray(raw.brevets) ? raw.brevets : []) {
    const type = String((item as Record<string, unknown>)?.type ?? '')
    if (!(type in BREVETS)) continue
    const def = BREVETS[type as BrevetKey]
    const number = clean((item as Record<string, unknown>)?.number, 40)
    if (brevets.some(b => b.type === type)) continue
    if (def.numberRequired && !number) {
      errors.brevets = `${def.numberLabel.replace(/ \(.*\)$/, '')} requis pour ${def.label}.`
      break
    }
    if (def.inami && !/^\d{11}$/.test(number.replace(/\D/g, ''))) {
      errors.brevets = `Le n° INAMI de ${def.label} comporte 11 chiffres.`
      break
    }
    const rawExpiry = String((item as Record<string, unknown>)?.expiresAt ?? '').trim()
    const expiresAt = parseExpiry(rawExpiry, now)
    if (rawExpiry && !expiresAt) {
      errors.brevets = `La date d'expiration de ${def.label} est invalide ou dépassée.`
      break
    }
    brevets.push({ type: type as BrevetKey, number: def.inami ? number.replace(/\D/g, '') : number, expiresAt })
  }
  if (!errors.brevets && functions.some(f => BREVET_REQUIRED_FUNCTIONS.includes(f)) && !brevets.length) {
    errors.brevets = 'Un brevet est requis pour les fonctions Secours et Formateur.'
  }

  if (motivation.length < 20) errors.motivation = 'Dites-nous en quelques phrases pourquoi vous souhaitez nous rejoindre.'

  if (Object.keys(errors).length) return { errors }
  return { errors, value: { firstName, lastName, email, phone, birthdate, address, postalCode, city, nationalRegister, functions, brevets, motivation, source } }
}

// ── Matricule, identifiant, civilité ─────────────────────────────────────────

/**
 * Matricule `AAAAMMJJNN` : NN démarre à 11 et suit le plus grand numéro du jour déjà utilisé (dans eBrigade).
 */
export const nextMatricule = (existing: Iterable<string>, now = new Date()): string => {
  const day = `${now.getFullYear()}${String(now.getMonth() + 1).padStart(2, '0')}${String(now.getDate()).padStart(2, '0')}`
  let max = 10
  for (const value of existing) {
    const text = String(value ?? '').trim()
    if (text.length === 10 && text.startsWith(day) && /^\d{10}$/.test(text)) max = Math.max(max, Number(text.slice(8)))
  }
  return `${day}${String(max + 1).padStart(2, '0')}`
}

const slug = (value: string) => value.normalize('NFD').replace(/\p{M}/gu, '').toLowerCase().replace(/[^a-z0-9]+/g, '')

/** `prenom.nom` sans accents ; un chiffre est ajouté si l'identifiant est pris. */
export const buildUsername = (firstName: string, lastName: string, taken: Iterable<string>): string => {
  const used = new Set([...taken].map(v => String(v).toLowerCase()))
  const base = `${slug(firstName.split(/[\s-]/)[0] || firstName)}.${slug(lastName)}`
  if (!used.has(base)) return base
  for (let i = 2; i < 100; i++) if (!used.has(`${base}${i}`)) return `${base}${i}`
  return `${base}${Date.now() % 10000}`
}

/** 9ᵉ chiffre du registre national : impair = homme, pair = femme. */
export const civilityFromNationalRegister = (value: string): { civility: 1 | 2, sex: 'M' | 'F' } | null => {
  const digits = normalizeNationalRegister(value)
  if (!/^\d{11}$/.test(digits)) return null
  return Number(digits[8]) % 2 === 1 ? { civility: 1, sex: 'M' } : { civility: 2, sex: 'F' }
}

/** Nom du dossier Nextcloud : `NOM Prénom`. */
export const folderNameFor = (firstName: string, lastName: string): string =>
  `${lastName.toUpperCase()} ${firstName}`.replace(/[\\/:*?"<>|#%]/g, '').replace(/\s+/g, ' ').trim()

/** Référence lisible `AP-AAAA-NNNN`. */
export const formatReference = (year: number, sequence: number): string => `AP-${year}-${String(sequence).padStart(4, '0')}`

/** Jours écoulés entre deux dates ISO (arrondi inférieur). */
export const daysSince = (iso: string | null | undefined, now = new Date()): number => {
  const time = iso ? Date.parse(iso) : NaN
  return Number.isFinite(time) ? Math.max(0, Math.floor((now.getTime() - time) / 86_400_000)) : 0
}

/** Checklist d'intégration : échéances en jours après la création des comptes. */
export const ONBOARDING_CHECKLIST = [
  { key: 'contact', day: 2, label: 'Premier contact', owner: 'VOCAN' },
  { key: 'welcome', day: 7, label: 'Accueil au centre', owner: 'VOCAN' },
  { key: 'uniform', day: 10, label: 'Tenue de base', owner: 'Logistique' },
  { key: 'badge', day: 14, label: 'Badge remis', owner: 'Logistique' },
  { key: 'first_duty', day: 30, label: 'Première garde et point d\'étape', owner: 'VOCAN' },
] as const
export type ChecklistKey = (typeof ONBOARDING_CHECKLIST)[number]['key']
export const isChecklistKey = (value: unknown): value is ChecklistKey => ONBOARDING_CHECKLIST.some(item => item.key === value)

/** Recherche publique INAMI pour vérifier un numéro déclaré. */
export const INAMI_SEARCH_URL = 'https://www.inami.fgov.be/fr/professionnels/sante/Pages/default.aspx'
